import { formatDate } from "utils/dateFormatter";

/*
 * The obstetrician's private rooms the maternity hospital named in PV1.9 (KIN-2034).
 *
 * Not the GP's choice and not a consultant they treat with: it is a Service Directory identifier
 * the hospital sends and we reflect back unchanged, so the only honest thing the form can do is
 * show what will be sent. Public patients have none, and a dropdown let a GP attach somebody
 * else's private rooms to one.
 *
 * The backend decides which message it comes from (KIN-2033) and sends it; this only describes it.
 */
export const describePrivateRooms = (privateRooms) => {
  if (!privateRooms?.surname) return null;

  const name = [privateRooms.firstName, privateRooms.surname]
    .filter(Boolean)
    .join(" ");
  const receivedAt = privateRooms.receivedAt
    ? formatDate(privateRooms.receivedAt)
    : null;

  return receivedAt
    ? `${name} (from the hospital's message of ${receivedAt})`
    : name;
};

const firstElement = (parent, localName) => {
  const elements = parent.getElementsByTagNameNS("*", localName);

  return elements.length === 0 ? null : elements[0];
};

/* The text of a child element, or null when it is absent or empty, so "not sent" stays "not sent". */
const childText = (parent, localName) => {
  const element = firstElement(parent, localName);
  const text = element?.textContent?.trim();

  return text ? text : null;
};

/**
 * Reads PV1.9 out of one stored hospital message, mirroring the backend's
 * `MncmsPrivateRooms.fromMessage` (KIN-2033).
 *
 * XCN.1 is what routes the rooms, so a PV1.9 without one is not something to send back and reads
 * here as absent. Matching on local names means a message stored with or without the HL7 namespace
 * reads the same.
 */
export const readPrivateRoomsFromMessage = (message) => {
  const xml = message?.hl7MessageContent;

  if (!xml || !xml.includes("PV1.9")) return null;

  const parsed = new DOMParser().parseFromString(xml, "text/xml");

  if (parsed.getElementsByTagName("parsererror").length > 0) return null;

  const privateRoomsElement = firstElement(parsed, "PV1.9");

  if (!privateRoomsElement) return null;

  const id = childText(privateRoomsElement, "XCN.1");

  if (!id) return null;

  const surnameElement = firstElement(privateRoomsElement, "XCN.2");

  return {
    id,
    surname: surnameElement ? childText(surnameElement, "FN.1") : null,
    firstName: childText(privateRoomsElement, "XCN.3"),
    idType: childText(privateRoomsElement, "XCN.13"),
    fromMessageControlId: message.messageControlId ?? null,
    receivedAt: message.receivedAt ?? null,
  };
};

/**
 * The private rooms named by the pregnancy's most recent hospital message — the same value the
 * backend reflects into PV1.9 when a visit is sent (`MncmsInboundMessageService
 * .findPrivateRoomsToReflect`: newest message first, falling through to older ones when the
 * newest carries no PV1.9).
 *
 * Derived on the client from the inbound messages the pregnancy already loads, because the only
 * endpoint that returns the parsed value is keyed on a consultation and this is a fact about the
 * pregnancy. The inbound list carries the raw HL7 (KIN-1012), so this reads the same PV1.9 out of
 * the same messages rather than inventing a value or needing a new endpoint.
 *
 * Null for a public patient, whose hospital named no rooms.
 */
export const findPrivateRoomsForPregnancy = (inboundMessages) => {
  if (!Array.isArray(inboundMessages)) return null;

  const newestFirst = [...inboundMessages].sort((first, second) =>
    String(second.receivedAt ?? "").localeCompare(
      String(first.receivedAt ?? "")
    )
  );

  // Stops at the first message that names rooms rather than parsing every message's HL7.
  for (const message of newestFirst) {
    const privateRooms = readPrivateRoomsFromMessage(message);

    if (privateRooms) return privateRooms;
  }

  return null;
};
